/* ──────────────────────────────────────────────────────────────
 *  MissingSORDialog — Shows missing SOR parameters after upload
 *
 *  Ported from BushingApp._show_missing_sor_dialog (optibush.py).
 * ────────────────────────────────────────────────────────────── */

import React, { useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import DownloadIcon from '@mui/icons-material/Download';
import { jsPDF } from 'jspdf';
import type { SORParseResult } from '@/types/sor';
import { DISPLAY_LABELS_META, META_DISPLAY } from '@/constants/fieldMappings';


/* ── Global ignore keys (same as optibush.py) ── */

const GLOBAL_IGNORE: Set<string> = new Set([
  'min_area_explicit',
  'core_od_min',
  'core_od_max',
  'min_area_calculated',
  'part_name',
  'sor_no',
  'detected_bushing_type',
]);

/* ── Missing-value detection ── */

function isMissing(v: unknown): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === 'number') return !isFinite(v);
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    return ['', 'none', 'nan', 'null', 'na', 'n/a'].includes(s);
  }
  return false;
}

export interface MissingSORData {
  meta: Record<string, string>;
  sorParts: Record<string, string[]>;
  missingPartNumbers?: string[];
}

/**
 * Detect missing parameters in a parsed SOR result.
 * Same logic as `BushingApp.missing_input_sor` in optibush.py.
 */
export function detectMissingSORParams(result: SORParseResult): MissingSORData {
  const missing: MissingSORData = { meta: {}, sorParts: {} };

  // ── Meta section ──
  if (result.meta) {
    const meta: Record<string, unknown> = { ...result.meta };
    for (const [k, v] of Object.entries(meta)) {
      if (GLOBAL_IGNORE.has(k)) continue;
      missing.meta[k] = isMissing(v) ? '' : String(v);
    }
  }

  // ── SOR parts ──
  for (const [pn, part] of Object.entries(result.sorParts)) {
    const partObj: Record<string, unknown> = { ...part };
    const partIgnore = new Set(GLOBAL_IGNORE);

    // Dynamic ignore per part (same conditional as optibush.py)
    const detected_bushing_type = partObj.detected_bushing_type;

    if (detected_bushing_type === 'A1' || detected_bushing_type === 'B1') {
      partIgnore.add('radial_ii');
      partIgnore.add('radial_ii_amp');
      partIgnore.add('flange_od');
      partIgnore.add('conical_ii');
    } 
    else if (detected_bushing_type === 'C1') {
      partIgnore.add('radial_ii');
      partIgnore.add('radial_ii_amp');
      partIgnore.add('conical_ii');
    } 
    else {
      partIgnore.add('flange_od');
    }

    const partMissing: string[] = [];
    for (const [k, v] of Object.entries(partObj)) {
      if (partIgnore.has(k)) continue;
      if (isMissing(v)) partMissing.push(k);
    }

    if (partMissing.length > 0) {
      missing.sorParts[pn] = partMissing;
    }
  }

  // Add missing part numbers from backend if present
  if (Array.isArray(result.missingPartNumbers) && result.missingPartNumbers.length > 0) {
    missing.missingPartNumbers = result.missingPartNumbers;
  }

  return missing;
}

/** Returns true when there are actual missing SOR-part fields. */
export function hasMissingParams(data: MissingSORData): boolean {
  return Object.keys(data.sorParts).length > 0 ||
    (Array.isArray(data.missingPartNumbers) && data.missingPartNumbers.length > 0);
}

/* ── Dialog component ── */

interface MissingSORDialogProps {
  open: boolean;
  onClose: () => void;
  data: MissingSORData | null;
}

const MissingSORDialog: React.FC<MissingSORDialogProps> = ({
  open,
  onClose,
  data,
}) => {
  const handleDownloadPDF = useCallback(() => {
    if (!data) return;
    const pdf = new jsPDF('p', 'mm', 'a4');
    const margin = 15;
    let y = margin;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const maxWidth = pageWidth - margin * 2;

    // Title
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.setTextColor(25, 118, 210);
    pdf.text('Missing SOR Parameters', margin, y);
    y += 10;

    // Info (meta) section
    const hasMetaEntries = Object.keys(data.meta).length > 0;
    if (hasMetaEntries) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(13);
      pdf.setTextColor(25, 118, 210);
      pdf.text('Info', margin, y);
      y += 6;

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      for (const [key, value] of Object.entries(data.meta)) {
        const label = META_DISPLAY[key] ?? key;
        const lines = pdf.splitTextToSize(`${label} \u2013 ${value || ''}`, maxWidth);
        pdf.text(lines, margin, y);
        y += lines.length * 4.5;
      }
      y += 4;
    }

    // Missing section
    const hasMissingSorParts = Object.keys(data.sorParts).length > 0;
    const hasMissingPartNumbers = Array.isArray(data.missingPartNumbers) && data.missingPartNumbers.length > 0;

    if (hasMissingSorParts || hasMissingPartNumbers) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(13);
      pdf.setTextColor(25, 118, 210);
      pdf.text('Missing', margin, y);
      y += 6;

      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);

      for (const [partNo, keys] of Object.entries(data.sorParts)) {
        pdf.setFont('helvetica', 'bold');
        pdf.text(`Part Number \u2013 ${partNo.toUpperCase()}`, margin, y);
        y += 5;

        pdf.setFont('helvetica', 'normal');
        for (const k of keys) {
          const label = DISPLAY_LABELS_META[k] ?? k;
          pdf.text(`\u2022 ${label}`, margin + 2, y);
          y += 4.5;
        }
        y += 3;
      }

      if (hasMissingPartNumbers) {
        pdf.setFont('helvetica', 'bold');
        pdf.text('Missing Part Numbers', margin, y);
        y += 5;

        pdf.setFont('helvetica', 'normal');
        for (const pn of data.missingPartNumbers!) {
          pdf.text(`\u2022 ${pn.toUpperCase()}`, margin + 2, y);
          y += 4.5;
        }
      }
    }

    pdf.save('missing_sor_parameters.pdf');
  }, [data]);

  if (!data) return null;

  const hasMetaEntries = Object.keys(data.meta).length > 0;
  const hasMissingSorParts = Object.keys(data.sorParts).length > 0;
  const hasMissingPartNumbers = Array.isArray(data.missingPartNumbers) && data.missingPartNumbers.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: { sx: { maxHeight: 600, borderRadius: 2 } },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          color: '#1976d2',
          fontSize: 18,
          py: 2,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        Missing SOR Parameters
        <IconButton
          onClick={handleDownloadPDF}
          size="small"
          title="Download as PDF"
          sx={{ color: '#1976d2' }}
        >
          <DownloadIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: 2, py: 2, fontSize: 14, lineHeight: 1.6 }}>
        <Box>
          {/* ── Info (meta) section ── */}
          {hasMetaEntries && (
            <Box sx={{ mb: 1.5 }}>
              <Typography
                sx={{ fontWeight: 700, fontSize: 16, mb: 1, color: '#1976d2' }}
              >
                Info
              </Typography>
              {Object.entries(data.meta).map(([key, value]) => (
                <Typography key={key} variant="body2" sx={{ lineHeight: 1.6 }}>
                  {META_DISPLAY[key] ?? key} – {value || ''}
                </Typography>
              ))}
            </Box>
          )}

          {/* ── Missing section ── */}
          {(hasMissingSorParts || hasMissingPartNumbers) && (
            <Box>
              <Typography
                sx={{ fontWeight: 700, fontSize: 16, mb: 1, color: '#1976d2' }}
              >
                Missing
              </Typography>
              {/* Show missing fields for present part numbers */}
              {Object.entries(data.sorParts).map(([partNo, keys]) => (
                <Box key={partNo} sx={{ mb: 1 }}>
                  <Typography
                    sx={{ fontWeight: 700, mt: 0.5, mb: 0.25 }}
                    variant="body2"
                  >
                    Part Number – {partNo.toUpperCase()}
                  </Typography>
                  {keys.map((k: string) => (
                    <Typography key={k} variant="body2" sx={{ pl: 0 }}>
                      • {DISPLAY_LABELS_META[k] ?? k}
                    </Typography>
                  ))}
                </Box>
              ))}
              {/* Show missing part numbers (not present at all) */}
              {hasMissingPartNumbers && (
                <Box sx={{ mb: 1 }}>
                  <Typography
                    sx={{ fontWeight: 700, mt: 0.5, mb: 0.25 }}
                    variant="body2"
                  >
                    Missing Part Numbers
                  </Typography>
                  {data.missingPartNumbers!.map((pn) => (
                    <Typography key={pn} variant="body2" sx={{ pl: 0 , textTransform: 'uppercase'}}>
                      • {pn} 
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{ textTransform: 'none',
            bgcolor: 'info.main',
            boxShadow: 4, 
            '&:hover': { bgcolor: 'info.dark', boxShadow: 4 },
          }}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(MissingSORDialog);
